import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { createUser, generatePassword } from '../../services/userManagementService';
import toast from 'react-hot-toast';

/**
 * UserCreateModal Component
 *
 * A reusable modal for creating new users (patients, physicians, sponsors).
 * It dynamically adjusts fields based on the role.
 */
const UserCreateModal = ({ open, onClose, onUserCreated, role }) => {
  const { profile } = useAuth();
  const { getEffectiveTenantId } = useView();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({});
  const [errors, setErrors] = useState({});

  const initialFormState = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    specialty: '', // Physician-specific
    organization_name: '', // Sponsor-specific
  };

  useEffect(() => {
    // Reset form when modal opens for a new role
    // Auto-generate password for patients
    const password = role === 'patient' ? generatePassword() : '';
    setFormState({ ...initialFormState, password });
    setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    // Clear validation error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formState.first_name) newErrors.first_name = 'First name is required';
    if (!formState.last_name) newErrors.last_name = 'Last name is required';
    if (!formState.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = 'Email is invalid';
    }
    // For patients, password is auto-generated, so skip validation
    // For other roles, password is required
    if (role !== 'patient' && (!formState.password || formState.password.length < 6)) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (role === 'physician' && !formState.specialty) {
      newErrors.specialty = 'Specialty is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    // Get effective tenant ID (from clinic selector if viewing as clinic)
    const effectiveTenantId = getEffectiveTenantId() || profile?.tenant_id;
    
    if (!effectiveTenantId) {
      toast.error('Cannot create user: No clinic selected. Please select a clinic first.');
      return;
    }

    setLoading(true);
    try {
      const userData = {
        tenantId: effectiveTenantId, // Use tenantId (not tenant_id) as expected by createUser
        tenant_role: role, // Use tenant_role as expected by createUser
        ...formState,
      };

      const result = await createUser(userData);

      // Log result for debugging
      console.log('[UserCreateModal] createUser result:', result);

      // Check for error - result should have { data, error } structure
      if (result && result.error) {
        // Error toast already shown by createUser service
        console.error('[UserCreateModal] User creation returned error:', result.error);
        
        // IMPORTANT: Even if there's an error in the response, if the user was actually created
        // (which we can't easily verify here), we should still refresh the list
        // The error might be from email sending or other non-critical operations
        // So we'll refresh anyway to show the new user, but log the error
        console.warn('[UserCreateModal] Error returned but refreshing list anyway (user may have been created)');
        onUserCreated(); // Refresh list to show new user if it was created
        onClose(); // Close modal
        return;
      }

      // Check if result exists (user was created successfully)
      if (!result) {
        console.error('[UserCreateModal] User creation failed: No response from server');
        toast.error('User creation failed: No response from server');
        return;
      }

      // Success - refresh the list and close modal
      // Success toast already shown by createUser service
      onUserCreated(); // Callback to refresh the user list
      onClose(); // Close the modal
    } catch (error) {
      console.error('Error creating user:', error);
      // Only show error toast if createUser service didn't already show one
      // (createUser service shows toast in catch block, but we want to avoid duplicate)
      const errorMessage = error?.message || 'Failed to create user';
      // Check if error toast was already shown by checking if it's a known error format
      if (!errorMessage.includes('Backend server is not available')) {
        toast.error(`Failed to create user: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // TODO: Fetch specialty options from the backend
  const specialties = [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add New {role.charAt(0).toUpperCase() + role.slice(1)}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="first_name"
              label="First Name"
              value={formState.first_name}
              onChange={handleChange}
              error={!!errors.first_name}
              helperText={errors.first_name}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="last_name"
              label="Last Name"
              value={formState.last_name}
              onChange={handleChange}
              error={!!errors.last_name}
              helperText={errors.last_name}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="email"
              label="Email Address"
              type="email"
              value={formState.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="password"
              label={role === 'patient' ? 'Password (Auto-generated)' : 'Password'}
              type="password"
              value={formState.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={role === 'patient' 
                ? 'A secure password has been auto-generated. The patient will receive this password via email.'
                : errors.password || 'Password must be at least 6 characters'}
              required
              disabled={role === 'patient'}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="phone"
              label="Phone Number"
              value={formState.phone}
              onChange={handleChange}
            />
          </Grid>
          {role === 'physician' && (
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.specialty}>
                <InputLabel>Specialty</InputLabel>
                <Select
                  name="specialty"
                  value={formState.specialty}
                  onChange={handleChange}
                  label="Specialty"
                >
                  {specialties.map((spec) => (
                    <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                  ))}
                </Select>
                {errors.specialty && <FormHelperText>{errors.specialty}</FormHelperText>}
              </FormControl>
            </Grid>
          )}
          {role === 'sponsor' && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="organization_name"
                label="Organization Name"
                value={formState.organization_name}
                onChange={handleChange}
                helperText="Leave blank for individual sponsors"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserCreateModal;
